import { useCallback, useEffect, useRef, useState } from "react";
import { debounce } from "lodash";
import type { BackendAttribute } from "./useInit";

export type AutoSaveStatus = "idle" | "saving" | "saved" | "error";

type SyncEngine = (args: {
    baselineAttributes: BackendAttribute[];
    setBaselineAttributes: React.Dispatch<React.SetStateAction<BackendAttribute[]>>;
    currentData: Record<string, any>;
    projectId: string;
}) => Promise<{ success: boolean; noChanges?: boolean; error?: unknown }>;

interface UseAutoSaveOptions {
    enabled: boolean;
    projectId?: string;
    watch: (callback: (formValues: Record<string, any>) => void) => { unsubscribe: () => void };
    getValues: () => Record<string, any>;
    baselineAttributes: BackendAttribute[];
    setBaselineAttributes: React.Dispatch<React.SetStateAction<BackendAttribute[]>>;
    syncEngine: SyncEngine;
    skipNextSaveRef: React.MutableRefObject<boolean>;
    debounceMs?: number;
    onError?: (message: string) => void;
}

export function useAutoSave({
    enabled,
    projectId,
    watch,
    getValues,
    baselineAttributes,
    setBaselineAttributes,
    syncEngine,
    skipNextSaveRef,
    debounceMs = 500,
    onError,
}: UseAutoSaveOptions) {
    const [saveStatus, setSaveStatus] = useState<AutoSaveStatus>("idle");
    const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const baselineRef = useRef(baselineAttributes);
    const syncEngineRef = useRef(syncEngine);

    baselineRef.current = baselineAttributes;
    syncEngineRef.current = syncEngine;

    const clearSavedTimer = useCallback(() => {
        if (savedTimerRef.current) {
            clearTimeout(savedTimerRef.current);
            savedTimerRef.current = null;
        }
    }, []);

    const markSaved = useCallback(() => {
        clearSavedTimer();
        setSaveStatus("saved");
        savedTimerRef.current = setTimeout(() => {
            setSaveStatus("idle");
            savedTimerRef.current = null;
        }, 2000);
    }, [clearSavedTimer]);

    const runSave = useCallback(
        async (currentData: Record<string, any>) => {
            if (!projectId) {
                return { success: false };
            }

            setSaveStatus("saving");
            const result = await syncEngineRef.current({
                baselineAttributes: baselineRef.current,
                setBaselineAttributes,
                currentData,
                projectId,
            });

            if (!result.success) {
                setSaveStatus("error");
                onError?.("Failed to save inspection changes");
                return result;
            }

            if (result.noChanges) {
                setSaveStatus((prev) => (prev === "saving" ? "idle" : prev));
                return result;
            }

            markSaved();
            return result;
        },
        [projectId, setBaselineAttributes, markSaved, onError],
    );

    const debouncedSaveRef = useRef(
        debounce((currentData: Record<string, any>) => {
            void runSave(currentData);
        }, debounceMs),
    );

    useEffect(() => {
        debouncedSaveRef.current = debounce((currentData: Record<string, any>) => {
            void runSave(currentData);
        }, debounceMs);
    }, [debounceMs, runSave]);

    useEffect(() => {
        return () => {
            debouncedSaveRef.current.cancel();
            clearSavedTimer();
        };
    }, [clearSavedTimer]);

    useEffect(() => {
        if (!enabled || !projectId) return;

        const subscription = watch((formValues) => {
            if (skipNextSaveRef.current) {
                skipNextSaveRef.current = false;
                return;
            }
            debouncedSaveRef.current(formValues);
        });

        return () => subscription.unsubscribe();
    }, [enabled, projectId, watch, skipNextSaveRef]);

    const flushSave = useCallback(async () => {
        debouncedSaveRef.current.cancel();
        return runSave(getValues());
    }, [getValues, runSave]);

    return {
        saveStatus,
        flushSave,
    };
}
